import { FC } from 'react';
import styled from '@emotion/styled';
import {
  Typography,
  Button,
  Select,
  StyledDiv,
} from '@taleemabad/dsm/components';
import { IStudentData, FlnCategory } from '@taleemabad/db';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';

// Styled components
const ContentContainer = styled.div`
  gap: var(--rk-spacing-lg);
  padding: var(--rk-spacing-md);
  margin-bottom: var(--rk-spacing-xxxxxxxl);

  ${mq(BreakpointSizes.xs)} {
    gap: var(--rk-spacing-xl);
    padding: 0;
    margin-bottom: var(--rk-spacing-xxxxxxl);
  }
`;

const SectionHeader = styled.div`
  margin-bottom: var(--rk-spacing-md);

  ${mq(BreakpointSizes.xs)} {
    margin-bottom: var(--rk-spacing-lg);
  }
`;

const StudentRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--rk-spacing-md);
  gap: var(--rk-spacing-lg);
  padding: var(--rk-spacing-md);
  background-color: var(--md-sys-color-surface-container-low);
  border-radius: var(--rk-spacing-md);
  border: 1px solid var(--md-sys-color-outline-variant);

  ${mq(BreakpointSizes.xs)} {
    flex-direction: column;
    padding: var(--rk-spacing-lg);
    background-color: var(--md-sys-color-surface);
  }
`;

const StudentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--rk-spacing-sm);
  flex: 1;

  ${mq(BreakpointSizes.xs)} {
    gap: var(--rk-spacing-md);
  }
`;

const CategorySelector = styled.div`
  display: flex;
  align-items: center;
  gap: var(--rk-spacing-lg);
  justify-content: space-between;

  ${mq(BreakpointSizes.xs)} {
    gap: var(--rk-spacing-md);
  }
`;

const CategoryBadge = styled.div<{ category: FlnCategory | null }>`
  padding: 4px 8px;
  border-radius: var(--rk-spacing-xl);
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: lowercase;
  white-space: nowrap;

  ${mq(BreakpointSizes.xs)} {
    padding: var(--rk-spacing-xs) var(--rk-spacing-md);
    font-size: 0.875rem;
  }

  ${({ category }) => {
    switch (category) {
      case FlnCategory.BEGINNER:
        return `
          background-color: #FFE5E5;
          color: #D32F2F;
        `;
      case FlnCategory.INTERMEDIATE:
        return `
          background-color: #FFF8E1;
          color: #F57C00;
        `;
      case FlnCategory.ADVANCED:
        return `
          background-color: #E8F5E8;
          color: #388E3C;
        `;
      case FlnCategory.GRADUATE:
        return `
          background-color: #F3E5F5;
          color: #7B1FA2;
        `;
      default:
        return `
          background-color: var(--md-sys-color-surface-variant);
          color: var(--md-sys-color-on-surface-variant);
        `;
    }
  }}
`;

const StyledButtonsContainer = styled.div`
  position: fixed;
  bottom: 0;
  right: 10px;
  display: flex;
  justify-content: end;
  gap: var(--rk-spacing-lg);
  padding: var(--rk-spacing-xl);
  z-index: 1;

  ${mq(BreakpointSizes.xs)} {
    bottom: 75px;
  }
`;
const ActionButtonContainer = styled.div`
  display: flex;
  gap: var(--rk-spacing-xl);
  justify-content: right;
  margin-right: var(--rk-spacing-xl);
  width: -webkit-fill-available;
  padding: var(--rk-spacing-xxl) 0;
  position: fixed;
  background-color: var(--md-ref-neutral-100);
  bottom: 0;
  z-index: 1;

  ${mq(BreakpointSizes.xs)} {
    margin-bottom: var(--rk-spacing-xxxxxxxl);
    justify-content: center;
    md-filled-button,
    md-outlined-button {
      flex: 1;
    }
  }
`;

// Interfaces
interface IStudentWithExamInfo extends IStudentData {
  examId?: string | null;
  category?: FlnCategory | null;
}

interface FlnResultsTemplateProps {
  students: IStudentWithExamInfo[];
  onStudentCategoryChange?: (studentId: string, category: FlnCategory) => void;
  onAddStudentClick?: () => void;
  loading?: boolean;
  isSaving?: boolean;
}

const categoryOptions = [
  { value: FlnCategory.BEGINNER, content: 'Beginner' },
  { value: FlnCategory.INTERMEDIATE, content: 'Intermediate' },
  { value: FlnCategory.ADVANCED, content: 'Advanced' },
  { value: FlnCategory.GRADUATE, content: 'Graduate' },
];

export const FlnResultsTemplate: FC<FlnResultsTemplateProps> = ({
  students,
  onStudentCategoryChange,
  onAddStudentClick,
}) => {
  const handleCategorySelect = (
    studentProfile: string,
    category: FlnCategory,
  ) => {
    onStudentCategoryChange?.(studentProfile, category);
  };

  return (
    <ContentContainer>
      <>
        <SectionHeader>
          <Typography variant="textTitleMedium">
            Student Category Assignment
          </Typography>
        </SectionHeader>

        {students.length === 0 && (
          <Typography variant="textBodyMedium">
            No students available. Please add students to assign categories.
          </Typography>
        )}
        {students.map((student) => (
          <StudentRow key={student.id}>
            <StudentInfo>
              <StyledDiv alignItem="center" gap={4}>
                <Typography variant="textBodyLargeOnSurfaceVariant">
                  Name:
                </Typography>
                <Typography variant="textBodyMedium">{student.name}</Typography>
              </StyledDiv>
            </StudentInfo>

            <CategorySelector>
              <Select
                key={student.id}
                defaultValue={student.category}
                label="Select Category"
                width="10px"
                variant="outlined"
                onChange={(option) =>
                  handleCategorySelect(
                    student.uuid || '',
                    option.value as FlnCategory,
                  )
                }
                options={categoryOptions}
              />

              <CategoryBadge category={student.category ?? null}>
                {student.category ? student.category : 'Unassigned'}
              </CategoryBadge>
            </CategorySelector>
          </StudentRow>
        ))}
      </>
      <ActionButtonContainer>
        <Button
          data-tour="add-student"
          onClick={onAddStudentClick}
          variant="outlined"
          icon="add"
          iconColor="--md-sys-color-primary"
          children="Add Student"
        />
      </ActionButtonContainer>
    </ContentContainer>
  );
};
